import {
	isValid,
	isAfter,
	isToday,
	isTomorrow,
	isYesterday,
	isThisWeek,
	isBefore,
	isSameDay,
	differenceInCalendarWeeks,
	differenceInCalendarYears,
	differenceInCalendarQuarters,
	startOfTomorrow,
	startOfToday,
	startOfYesterday,
} from 'date-fns';
import {
	START_DATE_TYPE,
	UPDATED,
	CREATED,
	DUE_DATE_TYPE,
	DATE_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { FormatRule, EvaluateHandler } from '../../../evaluate-format-rule/types.tsx';
import type { SUPPORTED_DATE_TYPES } from '../types.tsx';
import { DATE_VALUE_OPERATORS } from './constants.tsx';

type DateField = {
	fieldId: string;
	type: SUPPORTED_DATE_TYPES;
	value: string | null;
};

const evaluateHandler: EvaluateHandler<DateField> = {
	IS: (field, value) => {
		if (field.value && typeof value === 'string') {
			switch (value) {
				case DATE_VALUE_OPERATORS.TODAY:
					return isToday(new Date(field.value));
				case DATE_VALUE_OPERATORS.TOMORROW:
					return isTomorrow(new Date(field.value));
				case DATE_VALUE_OPERATORS.YESTERDAY:
					return isYesterday(new Date(field.value));
				case DATE_VALUE_OPERATORS.THIS_WEEK:
					return isThisWeek(new Date(field.value));
				case DATE_VALUE_OPERATORS.LAST_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) === -1;
				case DATE_VALUE_OPERATORS.NEXT_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) === 1;
				case DATE_VALUE_OPERATORS.THIS_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) === 0;
				case DATE_VALUE_OPERATORS.LAST_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) === -1;
				case DATE_VALUE_OPERATORS.NEXT_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) === 1;
				case DATE_VALUE_OPERATORS.THIS_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) === 0;
				case DATE_VALUE_OPERATORS.LAST_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) === -1;
				case DATE_VALUE_OPERATORS.NEXT_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) === 1;
				default: {
					const dateValue = new Date(value);
					if (isValid(dateValue)) {
						return isSameDay(dateValue, new Date(field.value));
					}
					return false;
				}
			}
		}
		return false;
	},
	IS_AFTER: (field, value) => {
		if (field.value && typeof value === 'string') {
			switch (value) {
				case DATE_VALUE_OPERATORS.TODAY:
					return isAfter(new Date(field.value), startOfToday());
				case DATE_VALUE_OPERATORS.TOMORROW:
					return isAfter(new Date(field.value), startOfTomorrow());
				case DATE_VALUE_OPERATORS.YESTERDAY:
					return isAfter(new Date(field.value), startOfYesterday());
				case DATE_VALUE_OPERATORS.THIS_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) > 0;
				case DATE_VALUE_OPERATORS.LAST_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) >= 0;
				case DATE_VALUE_OPERATORS.NEXT_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) > 1;
				case DATE_VALUE_OPERATORS.THIS_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) > 0;
				case DATE_VALUE_OPERATORS.LAST_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) >= 0;
				case DATE_VALUE_OPERATORS.NEXT_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) > 1;
				case DATE_VALUE_OPERATORS.THIS_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) > 0;
				case DATE_VALUE_OPERATORS.LAST_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) >= 0;
				case DATE_VALUE_OPERATORS.NEXT_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) > 1;
				default: {
					const dateValue = new Date(value);
					if (isValid(dateValue)) {
						return isAfter(new Date(field.value), dateValue);
					}
					return false;
				}
			}
		}
		return false;
	},
	IS_BEFORE: (field, value) => {
		if (field.value && typeof value === 'string') {
			switch (value) {
				case DATE_VALUE_OPERATORS.TODAY:
					return isBefore(new Date(field.value), startOfToday());
				case DATE_VALUE_OPERATORS.TOMORROW:
					return isBefore(new Date(field.value), startOfTomorrow());
				case DATE_VALUE_OPERATORS.YESTERDAY:
					return isBefore(new Date(field.value), startOfYesterday());
				case DATE_VALUE_OPERATORS.THIS_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) < 0;
				case DATE_VALUE_OPERATORS.LAST_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) < -1;
				case DATE_VALUE_OPERATORS.NEXT_WEEK:
					return differenceInCalendarWeeks(new Date(field.value), new Date()) < 1;
				case DATE_VALUE_OPERATORS.THIS_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) < 0;
				case DATE_VALUE_OPERATORS.LAST_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) < -1;
				case DATE_VALUE_OPERATORS.NEXT_QUARTER:
					return differenceInCalendarQuarters(new Date(field.value), new Date()) < 1;
				case DATE_VALUE_OPERATORS.THIS_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) < 0;
				case DATE_VALUE_OPERATORS.LAST_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) < -1;
				case DATE_VALUE_OPERATORS.NEXT_YEAR:
					return differenceInCalendarYears(new Date(field.value), new Date()) < 1;
				default: {
					const dateValue = new Date(value);
					if (isValid(dateValue)) {
						return isBefore(new Date(field.value), dateValue);
					}
					return false;
				}
			}
		}
		return false;
	},
	IS_NOT: (field, value) => !evaluateHandler.IS?.(field, value),
	IS_ON_OR_AFTER: (field, value) =>
		Boolean(evaluateHandler.IS?.(field, value) || evaluateHandler.IS_AFTER?.(field, value)),
	IS_ON_OR_BEFORE: (field, value) =>
		Boolean(evaluateHandler.IS?.(field, value) || evaluateHandler.IS_BEFORE?.(field, value)),
	IS_EMPTY: (field) => !field.value,
	IS_NOT_EMPTY: (field) => Boolean(field.value),
};

const evaluateFormatRule: FormatRule<DateField> = ({ field, operator, value }) => {
	const evaluateHandlerFn = evaluateHandler[operator];
	if (!evaluateHandlerFn) return false;
	return evaluateHandlerFn(field, value);
};

export const dateEvaluateFormatRule = {
	[START_DATE_TYPE]: evaluateFormatRule,
	[UPDATED]: evaluateFormatRule,
	[CREATED]: evaluateFormatRule,
	[DUE_DATE_TYPE]: evaluateFormatRule,
	[DATE_CF_TYPE]: evaluateFormatRule,
};
